/**
 * Pure helpers for the sandbox admin's messages (services/SandboxMessagesService.js): the kinds, the text's links,
 * the maintenance window in the viewer's own time zone, and which messages show as the app-wide banner.
 */

export const MESSAGE_KINDS = {
  news: { label: 'News', tone: 'news' },
  maintenance: { label: 'Maintenance', tone: 'maintenance' },
  incident: { label: 'Incident', tone: 'incident' },
  info: { label: 'Info', tone: 'info' },
};

export const INCIDENT_STATUS = {
  investigating: 'Investigating',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

export const kindLabel = (kind) => (MESSAGE_KINDS[kind] || MESSAGE_KINDS.info).label;

/** Plain text in segments `{ text }` / `{ text, href }`: only http(s) links, never markup (React escapes the rest). */
export function linkSegments(text) {
  const out = [];
  const source = String(text ?? '');
  let last = 0;
  for (const match of source.matchAll(/https?:\/\/[^\s<>"']+/g)) {
    const url = match[0].replace(/[.,;:!?)\]]+$/, '');
    if (match.index > last) { out.push({ text: source.slice(last, match.index) }); }
    out.push({ text: url, href: url });
    last = match.index + url.length;
  }
  if (last < source.length) { out.push({ text: source.slice(last) }); }
  return out;
}

const dayKey = (date, timeZone) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).format(date);

/** "today", "tomorrow", "yesterday" or "Thu 16 Oct", in the viewer's time zone. */
function dayWord(date, now, timeZone) {
  const key = dayKey(date, timeZone);
  const day = 86400000;
  if (key === dayKey(now, timeZone)) { return 'today'; }
  if (key === dayKey(new Date(now.getTime() + day), timeZone)) { return 'tomorrow'; }
  if (key === dayKey(new Date(now.getTime() - day), timeZone)) { return 'yesterday'; }
  return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone }).format(date);
}

/** "today 18:00–19:00", "tomorrow 23:00 – Fri 17 Oct 01:00", in the viewer's local time (`timeZone` for tests). */
export function windowText(window, { now = new Date(), timeZone } = {}) {
  if (!window?.start || !window?.end) { return ''; }
  const start = new Date(window.start);
  const end = new Date(window.end);
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone });
  const startDay = dayWord(start, now, timeZone);
  if (dayKey(start, timeZone) === dayKey(end, timeZone)) { return `${startDay} ${time.format(start)}–${time.format(end)}`; }
  return `${startDay} ${time.format(start)} – ${dayWord(end, now, timeZone)} ${time.format(end)}`;
}

/** The one-line summary: "Maintenance today 18:00–19:00 · Database upgrade", "Incident · Monitoring · Slow previews". */
export function messageHeadline(message, options) {
  const parts = [message.kind === 'maintenance' && message.window ? `Maintenance ${windowText(message.window, options)}` : kindLabel(message.kind)];
  if (message.kind === 'incident' && message.status) { parts.push(INCIDENT_STATUS[message.status] || message.status); }
  parts.push(message.title);
  return parts.join(' · ');
}

/** A markdown body as one plain line, for the closed banner: no headings, bullets, emphasis, code marks or link targets. */
export function plainLine(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line
      .replace(/^\s*(#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>\s*)/, '')
      .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/(\*\*|__|\*|_|`)(\S(?:.*?\S)?)\1/g, '$2')
      .replace(/^[-*_\u2014\s]+$/, '')
      .trim())
    .filter(Boolean)
    .join(' · ');
}

/** Whether a body has more than one line (paragraphs, a list): the closed banner flattens it, so "Read more" shows it. */
export const isMultiline = (text) => String(text || '').trim().includes('\n');

/** The messages for the banner: banner on, not dismissed (for this version), incidents first, then newest. */
export function bannerMessages(items) {
  const rank = (m) => (m.kind === 'incident' && m.status !== 'resolved' ? 0 : m.kind === 'maintenance' ? 1 : 2);
  return (items || [])
    .filter((m) => m.banner && !m.dismissed)
    .sort((a, b) => rank(a) - rank(b) || String(b.updatedAt || b.at).localeCompare(String(a.updatedAt || a.at)));
}
